import {
  PREFLIGHT_REPORT_NAVIGATION_PAGE,
  TASK_NAVIGATION_SECTION,
  type TaskNavigation,
} from "@nexus/contracts";
import type { AnyRouter } from "@tanstack/react-router";
import { TASK_SECTION_SEARCH_PARAM } from "./task-section";

interface AuthStore {
  activeTenantId: string | null;
  setActiveTenant: (tenantId: string) => void;
}

/**
 * Maps semantic page keys from TaskNavigation to frontend route paths.
 * A `$name` segment is filled from `navigation.params`; the remaining params
 * become search params. Extend this as new task types are wired.
 */
const PAGE_ROUTE_MAP: Record<string, string> = {
  opportunities: "/opportunities",
  "persona-opportunity-sets": "/persona-opportunities",
  "identity-hub": "/iam",
  workspace: "/workspace",
  admin: "/admin",
  [PREFLIGHT_REPORT_NAVIGATION_PAGE]: "/pre-flight/reports/$reportId",
};

const PATH_PARAM_PATTERN = /\$([A-Za-z0-9_]+)/g;

interface ResolvedPath {
  path: string;
  pathParams: Set<string>;
}

/** Null when the route needs a path param the task did not carry. */
function resolvePath(
  route: string,
  params: Record<string, string> | undefined,
): ResolvedPath | null {
  const pathParams = new Set<string>();
  let missing = false;

  const path = route.replace(PATH_PARAM_PATTERN, (_match, name: string) => {
    const value = params?.[name];
    if (!value) {
      missing = true;
      return "";
    }
    pathParams.add(name);
    return encodeURIComponent(value);
  });

  return missing ? null : { path, pathParams };
}

/**
 * Resolve a TaskNavigation to a concrete TanStack Router navigation.
 * Auto-switches tenant if the task belongs to a different tenant than the active one.
 */
export function resolveTaskNavigation(
  nav: TaskNavigation,
  router: AnyRouter,
  auth: AuthStore,
): void {
  const route = PAGE_ROUTE_MAP[nav.page];
  if (!route) return;

  const resolved = resolvePath(route, nav.params);
  if (!resolved) return;

  const search: Record<string, string> = {};
  if (nav.params) {
    for (const [key, value] of Object.entries(nav.params)) {
      if (resolved.pathParams.has(key)) continue;
      search[key] = value;
    }
  }
  if (nav.itemIds && nav.itemIds.length > 0) {
    search.highlight = nav.itemIds.join(",");
  } else if (nav.params?.scriptId) {
    // Older storyboard / hook-variation records omit itemIds; scriptId still
    // identifies the row to scroll to.
    search.highlight = nav.params.scriptId;
  } else if (nav.params?.briefId) {
    search.highlight = nav.params.briefId;
  }
  if (nav.section) {
    search[TASK_SECTION_SEARCH_PARAM] = nav.section;
  } else if (nav.page === "persona-opportunity-sets" && nav.params?.tab === "scripts") {
    // Workers used to replace navigation and drop `section`. Scripts-tab cards
    // still need the jump trigger so ScriptsTab can expand the target row.
    search[TASK_SECTION_SEARCH_PARAM] = TASK_NAVIGATION_SECTION.PERSONA_SCRIPTS;
  }

  const doNavigate = () => {
    // Ignore the current URL's search. List filters (`q`, status, facets) would
    // otherwise stay put and hide the row the card is trying to jump to.
    router.navigate({
      to: resolved.path,
      search: () => ({ ...search }),
    });
  };

  if (nav.tenantId && nav.tenantId !== auth.activeTenantId) {
    auth.setActiveTenant(nav.tenantId);
    queueMicrotask(doNavigate);
  } else {
    doNavigate();
  }
}
